/*
 *   File : order.jsx
 *   Author : https://evoqins.com
 *   Description : Orders with server-side pagination
 *   Version : 1.0.0
 */

import React, { useEffect, useState } from "react";
import { Filter } from "../../Components/Filter";
import { CustomSearchInput } from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Cta";
import { DataTableComponent } from "../../Components/DataTable";
// import { useNavigate } from "react-router-dom";
import { APIService } from "../../Service";
import Icon from "../../Components/icons";
import { Chip, EmptyScreen, Loader } from "../../Components/Other";
import { toast } from "react-toastify";

export default function SipBook() {
  // const navigate = useNavigate();

  // States
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sipList, setSipList] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filterData, setFilterData] = useState();
  const [loader, setLoader] = useState(false);
  const [btnLoader, setBtnloader] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const COLUMNS = [
    {
      name: "SIP Date",
      selector: (row) => row.sip_date, // ✅ maps to sip_date
      sortable: true,
      sortFunction: (a, b) => new Date(a.sip_date) - new Date(b.sip_date),
    },
    {
      name: "Due Date",
      selector: (row) => row.next_due, // ✅ maps to sip_date
      sortable: true,
      sortFunction: (a, b) => new Date(a.next_due) - new Date(b.next_due),
    },
    {
      name: "User Name",
      selector: (row) => row.name,
    },
    {
      name: "Investment",
      selector: (row) => row.investment_name,
    },
    {
      name: "Investment Type",
      cell: (row) => (
        <Chip
          borderColor="e-border-1px-blue"
          fontColor="e-text-blue-violet"
          fontWeight="e-font-weight-500"
          fontSize="e-font-14"
          label={row.investment_type}
        />
      ),
    },
    {
      name: "Amount",
      sortable: true,
      selector: (row) => (
        <p className="e-secondary-font-Inter">{row.amount_text}</p>
      ), // ✅ formatted string
    },
    {
      name: "Sip Status",
      cell: (row) => {
        switch (
          row.status?.toLowerCase() // ✅ use "status"
        ) {
          case "active":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Active"
                borderColor="e-border-1px-green"
                bgColor="e-bg-pale-green"
                fontColor="e-text-green"
              />
            );
          case "due":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Due"
                borderColor="e-border-1px-ruby"
                bgColor="e-bg-pale-ruby"
                fontColor="e-text-ruby"
              />
            );
          case "inactive":
            return (
              <Chip
                label="Inactive"
                borderColor="e-border-1px-independence"
                bgColor="e-bg-pale-independence"
                fontColor="e-text-independence"
                fontWeight="e-font-weight-500"
                fontSize="e-font-12"
              />
            );
          default:
            return <span>{row.status}</span>;
        }
      },
    },
  ];

  // Fetch filter data once
  useEffect(() => {
    _getFilterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch orders when page, rowsPerPage, or filters/search changes
  useEffect(() => {
    // if (searchValue.length > 0) return;
    setLoader(true);
    _getSipList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, searchValue, selectedFilters]);

  useEffect(() => {
    if (downloadUrl.length === 0) return;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [downloadUrl]);

  // API: Get filter data
  const _getFilterData = () => {
    const is_post = false;
    APIService(is_post, "sip/filter-data")
      .then((response) => {
        if (response.status_code === 200) {
          setFilterData(response.data);
        }
      })
      .catch((err) => console.error(err));
  };

  // API: Get sip Lists
  const _getSipList = () => {
    setBtnloader(false);
    const is_post = true;
    const { sip_status, ...restFilters } = selectedFilters;
    const payload = {
      page_num: currentPage,
      page_size: rowsPerPage,
      query: searchValue,
      status: sip_status,
      ...restFilters,
    };

    APIService(is_post, "sip/list", payload)
      .then((response) => {
        if (response.status_code === 200) {
          setIsEmpty(false);
          setSipList(response.data.sip || []);
          setTotalRows(response.data.total_count || 0);
        } else {
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoader(false));
  };

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedRows(selectedRows);
  };

  const _exportSipList = () => {
    setBtnloader(true);
    const is_post = true;
    const { sip_status, ...restFilters } = selectedFilters;
    const data = {
      query: searchValue,
      status: sip_status,
      ...restFilters,
      customer_id: selectedRows.map((r) => r.customer_id.toString()),
    };
    APIService(is_post, "sip/sip-download", data)
      .then((response) => {
        if (response.status_code === 200) {
          setDownloadUrl(response.data.url);
          toast.success(response.message, {
            type: "success",
            className: "e-toast",
          });
        } else {
          console.error(response);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setBtnloader(false));
  };

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Filters + Search */}
        <div className="col-12">
          <div className="row py-4">
            <div className="col-2">
              <Filter
                dateLabelName="SIP date"
                filters={filterData}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                showDateRange={true}
              />
            </div>
            <div className="offset-lg-5 offset-md-3 offset-2 col-lg-5 col-md-7 col-8">
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ flex: 1, marginRight: "12px" }}>
                  <CustomSearchInput
                    type="text"
                    id="search"
                    placeholder="Search with User name"
                    className="w-100 e-search-input-padding"
                    value={searchValue}
                    handleChange={(e) => setSearchValue(e)}
                    onKeyPress={() => _getSipList()}
                  />
                </div>
                <PrimaryButton
                  name="Download"
                  loading={btnLoader}
                  onPress={() => _exportSipList()}
                  ClassName="e-border-radius-pill e-border-1px-blue e-font-weight-500 e-font-14 py-2 bg-transparent e-text-blue-violet e-primary-font-poppins"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}

        {isEmpty ? (
          <div className="d-flex min-h-100vh justify-content-center align-items-center">
            <EmptyScreen width={200} message="No Data Found" />
          </div>
        ) : loader ? (
          <div className="d-flex min-h-100vh justify-content-center align-items-center">
            <Loader width={30} />
          </div>
        ) : (
          <div className="row w-100 d-flex ms-1">
            <DataTableComponent
              columns={COLUMNS}
              data={sipList}
              pagination
              selectableRows={true}
              onSelectedRowsChange={handleSelectedRowsChange}
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={rowsPerPage}
              currentPage={currentPage}
              onChangePage={(page) => setCurrentPage(page)}
              onChangeRowsPerPage={(newPerPage, page) => {
                setRowsPerPage(newPerPage);
                setCurrentPage(page || 1);
              }}
              sortIcon={<Icon icon="sort_col" size="20px" />}
              className="e-sub-admin-module-table"
            />
          </div>
        )}
      </div>
    </div>
  );
}
